import { Navigate, useNavigate } from '@tanstack/react-router'
import type {
  MembershipData,
  StartupData,
  StartupMemberData,
  UserData,
} from '@workspace/api-backend'
import { ENTITLEMENT_KIND, ENTITLEMENT_STATUS } from '@workspace/seedforge-shared'
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react'
import { IntercomFounderAttributes } from '../components/intercom-founder-attributes'
import { StealthChoiceGate } from '../features/stealth-choice/components/stealth-choice-gate'
import {
  type BillingStatusData,
  useActiveEntitlementsQuery,
  useBillingStatusQuery,
  useStartupProfileQuery,
} from '../hooks/api'
import { useBasePath } from '../hooks/use-base-path'
import { useAppContext } from './app-guard'

// ─── Types ───────────────────────────────────────────────────────────────────

export type PitchDeckStatus = 'NONE' | 'PROCESSING' | 'READY' | 'FAILED'

export interface StartupContextValue {
  user: UserData
  startup: StartupData
  members: StartupMemberData[]
  startups: MembershipData[]
  billing: BillingStatusData | null
  /**
   * Whether the startup has an ACTIVE INVESTOR_MATCHES_UNLOCK entitlement.
   * Sourced from a dedicated entitlements query (not from billing
   * status) — entitlement subsystem is independent of billing. Defaults to
   * false during the brief deploy window where new studio + old backend may
   * co-exist (the entitlements query 404s).
   */
  investorMatchesUnlocked: boolean
  hasCompletedPitchSession: boolean
  /** Whether the startup has any completed evaluation (council prescreen or pitch). */
  hasEvaluation: boolean
  pitchDeckStatus: PitchDeckStatus
  switchStartup: (startupId: string) => void
}

// ─── Context ─────────────────────────────────────────────────────────────────

export const StartupContext = createContext<StartupContextValue | null>(null)

export function useStartupContext() {
  const ctx = useContext(StartupContext)
  if (!ctx) throw new Error('useStartupContext must be used within /app/startup/$id')
  return ctx
}

// ─── Provider (handles loading + role guard) ─────────────────────────────────

export function StartupRouteGuard({
  startupId,
  children,
}: {
  startupId: string
  children: ReactNode
}) {
  const { user, memberships } = useAppContext()
  const {
    data: profileData,
    isPending: isProfilePending,
    isError: isProfileError,
  } = useStartupProfileQuery(startupId)
  const { data: billingData } = useBillingStatusQuery(startupId)
  const { data: entitlementsData, isError: isEntitlementsError } =
    useActiveEntitlementsQuery(startupId)
  const navigate = useNavigate()
  const basePath = useBasePath()

  const startups = memberships.filter((m) => m.type === 'startup')

  const switchStartup = useCallback(
    (newStartupId: string) => {
      const newPath = basePath.replace(/\/app\/startup\/[^/]+/, `/app/startup/${newStartupId}`)
      navigate({ to: newPath })
    },
    [basePath, navigate]
  )

  // Default to UNLOCKED on transient errors so a 5xx on the entitlements
  // endpoint can't silently lock out a paying founder. The data path still
  // requires an explicit ACTIVE row; error path is fail-open.
  const investorMatchesUnlocked = isEntitlementsError
    ? true
    : (entitlementsData?.data?.items?.some(
        (e) =>
          e.kind === ENTITLEMENT_KIND.INVESTOR_MATCHES_UNLOCK &&
          e.status === ENTITLEMENT_STATUS.ACTIVE
      ) ?? false)

  // Memoize the value object so consumers don't re-render on every parent
  // render. Hooks must run unconditionally — keep above the early returns,
  // and tolerate `profileData` being undefined during the loading phase
  // (the early returns below short-circuit before the value is read).
  const value = useMemo<StartupContextValue | null>(() => {
    if (!profileData) return null
    return {
      user,
      startup: profileData.startup,
      members: profileData.members,
      startups,
      billing: billingData?.data ?? null,
      investorMatchesUnlocked,
      hasCompletedPitchSession: profileData.hasCompletedPitchSession,
      hasEvaluation: profileData.hasEvaluation,
      pitchDeckStatus: profileData.pitchDeckStatus as PitchDeckStatus,
      switchStartup,
    }
  }, [user, profileData, startups, billingData?.data, investorMatchesUnlocked, switchStartup])

  if (isProfilePending) return null

  if (user.userType !== 'FOUNDER') {
    return <Navigate to="/app" />
  }

  // No access to this startup (403/404) — redirect to first available
  if (isProfileError || !profileData || !value) {
    const firstStartup = startups[0]
    if (firstStartup) {
      return <Navigate to="/app/startup/$id" params={{ id: firstStartup.id }} />
    }
    return <Navigate to="/app" />
  }

  // Founder must explicitly choose stealth/visible before accessing the app.
  // Existing startups were backfilled with stealthChoiceMadeAt = NULL and
  // is_stealth = true, so deal-flow stays empty until consent is recorded. The
  // gate renders as a modal overlay so the founder sees the app underneath.
  const needsStealthChoice = profileData.startup.stealthChoiceMadeAt === null

  return (
    <StartupContext.Provider value={value}>
      <IntercomFounderAttributes />
      {children}
      {needsStealthChoice && (
        <StealthChoiceGate
          startupId={profileData.startup.id}
          startupName={profileData.startup.name}
        />
      )}
    </StartupContext.Provider>
  )
}
